import { Globe, ExternalLink, MapPin, Navigation, Building2, Star } from "lucide-react";

function ensureUrl(url) {
  if (!url || typeof url !== "string") return "";
  const t = url.trim();
  return t.startsWith("http") ? t : `https://${t}`;
}

/**
 * Shared source links block — identical in Database and Pipeline views.
 * Shows: Official Website, OTA Sources, Consortium Source, Google Maps.
 */
export default function SourceLinksBlock({ sourceProfiles, hotel }) {
  const ai = sourceProfiles?.ai_enrichment || {};
  const ota = sourceProfiles?.ota_data || {};
  const links = sourceProfiles?.source_links || {};

  // Official Website — from source_links, or fallback to ai_enrichment.official_website or hotel fields
  const officialUrl = links.official_website?.url
    || ai.official_website?.url
    || (ai.website?.url && ai.website.confidence >= 0.7 ? ai.website.url : "")
    || hotel?.primary_website || hotel?.derived_website || "";
  const officialConf = links.official_website?.confidence ?? ai.official_website?.confidence ?? 0;
  const officialSource = links.official_website?.source || ai.official_website?.source || hotel?.website_source || "";

  // OTA Sources
  const otaSources = (ota.ota_sources || (ota.sources_found || []).map(s => ({
    source_name: s.source_name || s.source || "",
    property_url: s.property_url || s.source_url || s.estimated_url || "",
    is_property_page: s.is_property_page ?? true,
    confidence: s.confidence || 0,
  }))).filter(s => s.confidence >= 0.3 && (s.property_url || s.source_url));

  // Consortium
  const consortium = links.consortium_source || {};
  const consortiumUrl = consortium.url || "";
  const consortiumName = consortium.name || hotel?.consortium || "";

  // Google Maps
  const maps = links.google_maps || {};
  const mapsUrl = maps.url || ai.location_intelligence?.google_maps_link || (ai.location || {}).google_maps_link || "";
  const mapsQuery = maps.query || ai.location_intelligence?.maps_query || "";

  const hasAnything = officialUrl || otaSources.length > 0 || consortiumUrl || mapsUrl;
  if (!hasAnything) return null;

  return (
    <div className="space-y-2" data-testid="source-links-block">
      {/* Official Website — visually prominent */}
      {officialUrl && (
        <div className="flex items-center gap-2 bg-green-500/[0.04] rounded-lg px-3 py-2 border border-green-500/10" data-testid="official-website-link">
          <Building2 className="w-4 h-4 text-green-400/60 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-green-400/70 text-[10px] font-semibold uppercase tracking-wider">Official Website</span>
              {officialSource && (
                <span className={`text-[8px] px-1.5 py-0.5 rounded ${
                  officialSource === "confirmed" ? "bg-green-500/15 text-green-400/70" :
                  officialSource === "imported" ? "bg-blue-500/10 text-blue-400/60" :
                  officialSource === "derived_from_email" ? "bg-amber-500/10 text-amber-400/60" :
                  "bg-purple-500/10 text-purple-400/60"
                }`}>{officialSource.replace(/_/g, " ")}</span>
              )}
              {officialConf > 0 && <span className="text-[8px] text-white/20">{Math.round(officialConf * 100)}%</span>}
            </div>
            <a href={ensureUrl(officialUrl)} target="_blank" rel="noopener noreferrer" className="text-green-400/80 hover:text-green-400 text-xs font-medium truncate block mt-0.5">
              {officialUrl.replace(/^https?:\/\/(www\.)?/, "").slice(0, 50)}
              <ExternalLink className="w-3 h-3 inline ml-1 -mt-0.5" />
            </a>
          </div>
        </div>
      )}

      {/* OTA Sources */}
      {otaSources.length > 0 && (
        <div className="space-y-1" data-testid="ota-links">
          {otaSources.map((s, i) => (
            <div key={i} className="flex items-center justify-between bg-white/[0.02] rounded-lg px-3 py-1.5">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Globe className="w-3 h-3 text-teal-400/40 shrink-0" />
                <span className="text-white/50 text-[11px] font-medium capitalize">{s.source_name}</span>
                {s.is_property_page === true && <span className="text-[7px] px-1 py-0.5 rounded bg-green-500/10 text-green-400/50 shrink-0">Property</span>}
              </div>
              <a href={ensureUrl(s.property_url || s.source_url)} target="_blank" rel="noopener noreferrer" className="text-blue-400/50 hover:text-blue-400/70 text-[11px] flex items-center gap-1 shrink-0">
                Offnen <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </div>
          ))}
        </div>
      )}

      {/* Consortium */}
      {consortiumUrl && consortiumName && (
        <div className="flex items-center justify-between bg-amber-500/[0.03] rounded-lg px-3 py-1.5 border border-amber-500/8" data-testid="consortium-link">
          <div className="flex items-center gap-2">
            <Star className="w-3 h-3 text-amber-400/40 shrink-0" />
            <span className="text-amber-400/60 text-[10px] font-medium">{consortiumName}</span>
            <span className="text-[7px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400/50">Consortium</span>
          </div>
          <a href={ensureUrl(consortiumUrl)} target="_blank" rel="noopener noreferrer" className="text-blue-400/50 hover:text-blue-400/70 text-[11px] flex items-center gap-1 shrink-0">
            Offnen <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>
      )}

      {/* Google Maps */}
      {mapsUrl && (
        <div className="flex items-center justify-between bg-blue-500/[0.03] rounded-lg px-3 py-1.5 border border-blue-500/8" data-testid="maps-link">
          <div className="flex items-center gap-2">
            <Navigation className="w-3 h-3 text-blue-400/50 shrink-0" />
            <span className="text-blue-400/60 text-[10px] font-medium">Google Maps</span>
            {mapsQuery && <span className="text-[8px] text-white/20">"{mapsQuery}"</span>}
          </div>
          <a href={ensureUrl(mapsUrl)} target="_blank" rel="noopener noreferrer" className="text-blue-400/50 hover:text-blue-400/70 text-[11px] flex items-center gap-1 shrink-0">
            Offnen <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>
      )}
    </div>
  );
}
